import { Link } from "react-router-dom";
import { Ambulance, Stethoscope, HeartPulse, Baby, Users2, ShieldCheck, Activity, Timer, ArrowRight } from "lucide-react";
import PageHeader from "@/components/site/PageHeader";
import SITE from "@/lib/site";

const services = [
  {
    icon: Ambulance,
    title: "Emergency Ambulance (BLS)",
    tag: "Priority dispatch",
    desc: "Basic Life Support ambulances for fractures, accidents and acute but non-critical patients. Dispatched in minutes.",
    features: ["Oxygen support", "Automated External Defibrillator", "Trained EMT onboard", "Stretcher + wheelchair"],
  },
  {
    icon: Stethoscope,
    title: "Advanced Life Support (ALS)",
    tag: "Critical care",
    desc: "Fully equipped ALS units for cardiac, trauma and critical events with paramedics trained in advanced protocols.",
    features: ["Cardiac monitor", "Defibrillator", "Ventilator capable", "Paramedic + EMT crew"],
  },
  {
    icon: HeartPulse,
    title: "ICU Ambulance",
    tag: "Mobile ICU",
    desc: "Mobile intensive care for patient transfers and critical events. Designed around stability and continuity.",
    features: ["ICU-grade ventilator", "Multi-parameter monitor", "Infusion pumps", "Doctor-on-board option"],
  },
  {
    icon: Baby,
    title: "Neonatal & Pediatric",
    tag: "Specialised",
    desc: "Specialised transport for newborns and children, including incubator-equipped transfers and pediatric crews.",
    features: ["Transport incubator", "Pediatric monitors", "Neonatal ventilator", "Pediatric paramedic"],
  },
  {
    icon: Activity,
    title: "Non-Emergency Patient Transport",
    tag: "Scheduled",
    desc: "Reliable scheduled transport for dialysis, chemotherapy, discharges and inter-hospital transfers.",
    features: ["Advance booking", "Return trip option", "Wheelchair friendly", "Flexible routes"],
  },
  {
    icon: Users2,
    title: "Event & On-site Medical Cover",
    tag: "Standby",
    desc: "Standby ambulances and crews for marathons, film sets, corporate campuses, sports and public events.",
    features: ["Dedicated crew", "On-site medic", "Custom hours", "Compliance paperwork"],
  },
  {
    icon: ShieldCheck,
    title: "Corporate & Insurance Plans",
    tag: "Plans",
    desc: "Tailored emergency coverage for employees, residents and insured members with unified dispatch.",
    features: ["API integration", "Monthly reports", "Priority dispatch", "Discounted rates"],
  },
  {
    icon: Timer,
    title: "Long-distance Transfer",
    tag: "Inter-city",
    desc: "Inter-city medical transfers by road or air coordination for stable patients needing specialised care.",
    features: ["Route planning", "Fuel & toll covered", "Crew rest logistics", "Air-ambulance referral"],
  },
];

export default function Services() {
  return (
    <div data-testid="services-page">
      <PageHeader
        eyebrow="Services"
        title="A complete ambulance network — for every kind of moment."
        description="From acute emergencies to scheduled transfers and on-site medical cover, Ambuly gives you one reliable partner for every medical transport need."
      />

      <section className="py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {services.map((s, i) => (
              <article
                key={s.title}
                data-testid={`services-card-${i}`}
                className="group relative bg-white border border-[#D5DDD7] rounded-2xl p-8 hover:shadow-xl transition-all duration-300"
              >
                <div className="flex items-center justify-between">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[#EDF1ED] text-[#1A3A32] group-hover:bg-[#1A3A32] group-hover:text-white transition-colors">
                    <s.icon className="h-6 w-6" />
                  </span>
                  <span className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#495D56] bg-[#EDF1ED] px-2.5 py-1 rounded-full">{s.tag}</span>
                </div>
                <h3 className="mt-6 text-2xl font-[Outfit] font-semibold text-[#14241F]">{s.title}</h3>
                <p className="mt-2 text-[#495D56]">{s.desc}</p>
                <ul className="mt-5 grid grid-cols-2 gap-x-4 gap-y-2 text-sm text-[#14241F]">
                  {s.features.map((f) => (
                    <li key={f} className="flex items-start gap-2">
                      <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#E5383B] flex-shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl overflow-hidden bg-[#EDF1ED] p-10 sm:p-14 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#1A3A32]">Not sure what you need?</p>
              <h3 className="mt-3 text-3xl sm:text-4xl font-[Outfit] font-semibold text-[#14241F]">Tell us about the patient. We'll recommend the right ambulance.</h3>
              <p className="mt-4 text-[#495D56] text-lg max-w-2xl">Our dispatch team will assess the situation and match the right crew, equipment and hospital — in minutes.</p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/contact" data-testid="services-book-btn" className="inline-flex items-center justify-center bg-[#E5383B] hover:bg-[#BA1826] text-white rounded-lg px-6 py-3 font-semibold">Book ambulance <ArrowRight className="ml-2 h-4 w-4" /></Link>
                <a href={`tel:${SITE.legal.emergencyLine}`} data-testid="services-call-btn" className="inline-flex items-center justify-center border-2 border-[#1A3A32] text-[#1A3A32] hover:bg-white rounded-lg px-6 py-3 font-semibold">Call {SITE.legal.emergencyLine}</a>
              </div>
            </div>
            <div className="lg:col-span-5">
              <img src={SITE.media.emergency} alt="Hospital emergency entrance" className="w-full h-72 sm:h-80 object-cover rounded-2xl" loading="lazy" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
